import { useCallback, useEffect, useState } from "react";
import { getConversation } from "../../api/secure-messaging-api";
import LoadSpinner from "../../components/load-spinner";
import { formatDate } from "../../constants/date";
import events from "../../constants/events";
import translate from "../../constants/translations";
import type { CustomerContext, Message } from "../../types";
import type {
	ClaimInfo,
	ConversationCategory,
} from "../new-message/start-conversation";
import SendReply from "../send-reply/send-reply";
import ConversationClosedInformation from "./conversation-closed-information";
import ConversationStatusIndicator from "./conversation-status-indicator";
import MessageContent from "./message-content/message-content";

const expanderPanelTitleButtonStyles: any = {
	"--ids-panel-title-border-radius": 0,
	"--ids-panel-expandable-is-open-title-border-radius": 0,
};

const expanderPanelContentStyles: any = {
	"--ids-panel-expandable-content-border-radius": 0,
};

const expanderPanelStyles: any = {
	"--ids-panel-border-radius": 0,
};

interface ConversationExpanderProps {
	customer: CustomerContext;
	cultureCode: string;
	conversationId: string;
	subject: string;
	isClosed: boolean;
	hasOneOrMoreUnseenMessages: boolean;
	activityDate: Date;
	claim?: ClaimInfo;
}

export default function ConversationExpander({
	conversationId,
	customer,
	cultureCode,
	subject,
	isClosed,
	hasOneOrMoreUnseenMessages,
	activityDate,
	claim,
}: ConversationExpanderProps) {
	const [expanded, setExpanded] = useState(false);
	const [newestMessageId, setNewestMessageId] = useState("");
	const [messages, setMessages] = useState<Message[]>([]);
	const [loading, setLoading] = useState(false);
	const [prefilledReply, setPrefilledReply] = useState("");
	const [category, setCategory] = useState<ConversationCategory | undefined>(
		undefined,
	);

	const t = (term: string): string => {
		return translate(cultureCode, term);
	};

	const refreshConversation = useCallback(async () => {
		setLoading(true);
		try {
			const conversation = await getConversation(customer, conversationId);
			const newestMessage = conversation.messages.find(
				(message) => message.messageId === conversation.newestMessageId,
			);
			const prefilledReply = newestMessage ? newestMessage.prefilledReply : "";
			setNewestMessageId(conversation.newestMessageId);
			setMessages(conversation.messages);
			setLoading(false);
			setPrefilledReply(prefilledReply);
			setCategory(
				conversation.conversationCategory as ConversationCategory | undefined,
			);
		} catch (error) {
			setLoading(false);
			console.error(error);
		}
	}, [customer, conversationId]);

	const toggleExpanded = () => {
		if (!expanded) {
			refreshConversation();
			window.dispatchEvent(new Event(events.replyMessageStarted));
		}
		setExpanded(!expanded);
	};

	const togglerStateClass = expanded ? "is-open" : "is-closed";

	return (
		<div
			key={`${conversationId}-${togglerStateClass}`}
			className={`if panel is-expandable light secure-messaging-component__conversation-expander ${togglerStateClass}`}
			style={expanderPanelStyles}
		>
			<button
				type="button"
				onClick={toggleExpanded}
				className="if title u-text-left secure-messaging-component__conversation-expander"
				style={expanderPanelTitleButtonStyles}
				aria-expanded={expanded}
				aria-controls={`conversation-content-${conversationId}`}
			>
				<span className="if grid across">
					<span className="if row">
						<span className="if col-10--smlr col-12--xs">
							<span
								id="subject"
								className="if mypages-offers-messages__subject"
							>
								{subject}
							</span>
							{claim && (
								<span className="if text bold secure-messaging-component__conversation-header-details">
									{`${t("Claim")} - ${claim.id}`}
								</span>
							)}
							<ConversationStatusIndicator
								hasOneOrMoreUnseenMessages={hasOneOrMoreUnseenMessages}
								cultureCode={cultureCode}
								isClosed={isClosed}
							/>
						</span>
						<span className="if col-2--smlr col-12--xs u-text-right--smlr">
							<span id="start-date">
								{formatDate(activityDate, cultureCode)}
							</span>
						</span>
					</span>
				</span>
			</button>
			<div
				className="if content"
				id={`conversation-content-${conversationId}`}
				style={expanderPanelContentStyles}
			>
				{loading ? (
					<LoadSpinner />
				) : (
					<div>
						{messages.map((message) => (
							<MessageContent
								key={`${conversationId}${message.sender}${message.timestamp.toString()}`}
								message={message}
								customer={customer}
								cultureCode={cultureCode}
								focused={newestMessageId === message.messageId}
								expanded={expanded}
							/>
						))}
						{isClosed ? (
							<ConversationClosedInformation cultureCode={cultureCode} />
						) : (
							<SendReply
								customer={customer}
								cultureCode={cultureCode}
								conversationId={conversationId}
								expanded={expanded}
								prefilledReply={prefilledReply}
								category={category}
								onReplySent={refreshConversation}
							/>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
